import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { ArrowLeft, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { professionalCategories, companySectors, italianProvinces, listingPriorities } from '@/lib/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateListing = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    sector: '',
    listing_type: '',
    priority: 'media',
    location_type: 'legal',
    location_country: '',
    location_province: '',
    location_city: ''
  });

  // Use imported constants
  const categories = professionalCategories;
  const sectors = companySectors;
  const priorities = listingPriorities;

  // Fetch company profile to get headquarters info
  useEffect(() => {
    if (user?.user_type === 'company') {
      fetchCompanyProfile();
    }
  }, [user]);

  const fetchCompanyProfile = async () => {
    try {
      const response = await axios.get(`${API}/profiles/company`);
      setCompanyProfile(response.data);
      // Pre-fill location with legal headquarters
      if (response.data.legal_country) {
        setFormData(prev => ({
          ...prev,
          location_country: response.data.legal_country,
          location_province: response.data.legal_province || '',
          location_city: response.data.legal_city || ''
        }));
      }
    } catch (error) {
      console.log('No company profile found');
    }
  };

  const handleLocationTypeChange = (locationType) => {
    setFormData(prev => ({ ...prev, location_type: locationType }));
    
    if (companyProfile) {
      if (locationType === 'legal') {
        setFormData(prev => ({
          ...prev,
          location_type: locationType,
          location_country: companyProfile.legal_country || 'Italia',
          location_province: companyProfile.legal_province || '',
          location_city: companyProfile.legal_city || ''
        }));
      } else if (locationType === 'operational' && companyProfile.has_operational_hq) {
        setFormData(prev => ({
          ...prev,
          location_type: locationType,
          location_country: companyProfile.operational_country || 'Italia',
          location_province: companyProfile.operational_province || '',
          location_city: companyProfile.operational_city || ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category || !formData.sector || !formData.listing_type) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    // Validate location for company listings
    if (user.user_type === 'company' && (!formData.location_province || !formData.location_city)) {
      toast.error('Indica dove richiedi la prestazione (provincia e comune)');
      return;
    }

    setCreating(true);
    try {
      const dataToSend = { ...formData };
      // Only send priority for company users
      if (user.user_type !== 'company') {
        delete dataToSend.priority;
      }
      await axios.post(`${API}/listings`, dataToSend);
      toast.success('Annuncio creato con successo!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating listing:', error);
      toast.error('Errore nella creazione dell\'annuncio');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-8 mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-6 text-slate-600 hover:text-[#344B71]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>

          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-playfair font-bold text-[#344B71] mb-2" data-testid="create-listing-title">
              {user.user_type === 'professional' ? 'Crea Nuova Offerta' : 'Crea Nuova Richiesta'}
            </h1>
            <p className="text-slate-600">
              {user.user_type === 'professional' 
                ? 'Pubblica un annuncio per offrire i tuoi servizi'
                : 'Pubblica un annuncio per trovare professionisti'}
            </p>
          </div>

          <Card className="p-8 bg-white">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="listing_type" className="text-sm font-medium text-slate-700">
                  Tipo di Annuncio *
                </Label>
                <Select 
                  value={formData.listing_type} 
                  onValueChange={(value) => setFormData({...formData, listing_type: value})}
                >
                  <SelectTrigger className="mt-1 h-12" data-testid="listing-type-select">
                    <SelectValue placeholder="Seleziona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offer">Offerta di Servizio</SelectItem>
                    <SelectItem value="request">Richiesta di Servizio</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="title" className="text-sm font-medium text-slate-700">
                  Titolo dell'Annuncio *
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="es. Consulenza strategica per PMI"
                  required
                  className="mt-1 h-12"
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label htmlFor="category" className="text-sm font-medium text-slate-700">
                  Categoria Professionale *
                </Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({...formData, category: value})}
                >
                  <SelectTrigger className="mt-1 h-12" data-testid="category-select">
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="sector" className="text-sm font-medium text-slate-700">
                  Settore *
                </Label>
                <Select 
                  value={formData.sector} 
                  onValueChange={(value) => setFormData({...formData, sector: value})}
                >
                  <SelectTrigger className="mt-1 h-12" data-testid="sector-select">
                    <SelectValue placeholder="Seleziona settore" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors.map((sector) => (
                      <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                  Descrizione Dettagliata *
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={8}
                  placeholder="Descrivi in dettaglio il servizio offerto o richiesto, le competenze necessarie, le modalità di collaborazione..."
                  required
                  className="mt-1"
                  data-testid="description-input"
                />
              </div>

              {/* Priority selector - only for companies */}
              {user.user_type === 'company' && (
                <div>
                  <Label htmlFor="priority" className="text-sm font-medium text-slate-700">
                    Priorità dell'Annuncio
                  </Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Seleziona la priorità per evidenziare l'urgenza della tua richiesta
                  </p>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(value) => setFormData({...formData, priority: value})}
                  >
                    <SelectTrigger className="mt-1 h-12" data-testid="priority-select">
                      <SelectValue placeholder="Seleziona priorità" />
                    </SelectTrigger>
                    <SelectContent>
                      {priorities.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          <span className={p.color}>{p.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Location Section - Only for company listings */}
              {user.user_type === 'company' && (
                <div className="pt-4 border-t border-slate-200">
                  <div className="flex items-center space-x-2 mb-4">
                    <MapPin className="w-5 h-5 text-slate-700" />
                    <h3 className="text-lg font-semibold text-slate-900">Luogo della Prestazione *</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    Indica dove richiedi che venga svolta la prestazione professionale
                  </p>

                  {/* Location Type Selection */}
                  <div className="mb-4">
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">
                      Sede di riferimento
                    </Label>
                    <div className="flex space-x-4">
                      <label className={`flex-1 flex items-center justify-center p-4 rounded-lg border-2 cursor-pointer transition-all ${formData.location_type === 'legal' ? 'border-[#344B71] bg-[#344B71]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                        <input
                          type="radio"
                          name="location_type"
                          value="legal"
                          checked={formData.location_type === 'legal'}
                          onChange={() => handleLocationTypeChange('legal')}
                          className="sr-only"
                        />
                        <span className="text-sm font-medium">Sede Legale</span>
                      </label>
                      {companyProfile?.has_operational_hq && (
                        <label className={`flex-1 flex items-center justify-center p-4 rounded-lg border-2 cursor-pointer transition-all ${formData.location_type === 'operational' ? 'border-[#344B71] bg-[#344B71]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                          <input
                            type="radio"
                            name="location_type"
                            value="operational"
                            checked={formData.location_type === 'operational'}
                            onChange={() => handleLocationTypeChange('operational')}
                            className="sr-only"
                          />
                          <span className="text-sm font-medium">Sede Operativa</span>
                        </label>
                      )}
                      <label className={`flex-1 flex items-center justify-center p-4 rounded-lg border-2 cursor-pointer transition-all ${formData.location_type === 'custom' ? 'border-[#344B71] bg-[#344B71]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                        <input
                          type="radio"
                          name="location_type"
                          value="custom"
                          checked={formData.location_type === 'custom'}
                          onChange={() => handleLocationTypeChange('custom')}
                          className="sr-only"
                        />
                        <span className="text-sm font-medium">Altra Sede</span>
                      </label>
                    </div>
                  </div>

                  {/* Location Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Paese *</Label>
                      <Select 
                        value={formData.location_country || 'Italia'} 
                        onValueChange={(value) => setFormData({...formData, location_country: value})}
                      >
                        <SelectTrigger className="mt-1 h-12">
                          <SelectValue placeholder="Seleziona paese" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Italia">Italia</SelectItem>
                          <SelectItem value="Altro">Altro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Provincia *</Label>
                      <Select 
                        value={formData.location_province || ''} 
                        onValueChange={(value) => setFormData({...formData, location_province: value})}
                      >
                        <SelectTrigger className="mt-1 h-12">
                          <SelectValue placeholder="Seleziona provincia" />
                        </SelectTrigger>
                        <SelectContent>
                          {italianProvinces.map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Comune *</Label>
                      <Input
                        value={formData.location_city || ''}
                        onChange={(e) => setFormData({...formData, location_city: e.target.value})}
                        placeholder="es. Milano"
                        className="mt-1 h-12"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-4 pt-6 border-t border-slate-200">
                <Button
                  type="button"
                  onClick={() => navigate(-1)}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 rounded-xl"
                  data-testid="cancel-button"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  className="bg-[#344B71] hover:bg-[#344B71] text-white px-8 rounded-xl shadow-lg hover:shadow-xl transition-all"
                  data-testid="create-button"
                >
                  {creating ? 'Creazione...' : 'Pubblica Annuncio'}
                </Button>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    </Layout>
  );
};

export default CreateListing;
